import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Header from '../common/Header';
import api, { getStaticBaseUrl } from '../../services/api';
import toast from 'react-hot-toast';
import { FaCalendarCheck, FaUserGraduate, FaCheckCircle, FaTimesCircle, FaTimes } from 'react-icons/fa';

const StudentDashboard = () => {
    const { user } = useAuth();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Poster Popup States
    const [showPosterPopup, setShowPosterPopup] = useState(false);
    const [selectedPoster, setSelectedPoster] = useState('');

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await api.get('/events');
            setEvents(response.data.events);
        } catch (error) {
            toast.error('Failed to fetch events');
        } finally {
            setLoading(false);
        }
    };

    const handleViewPoster = (posterUrl) => {
        setSelectedPoster(posterUrl);
        setShowPosterPopup(true);
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>;
    }

    return (
        <div>
            <Header title="Student Dashboard" />
            <div className="container">
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="label"><FaCalendarCheck /> Total Events</div>
                        <div className="value">{events.length}</div>
                    </div>
                    <div className="stat-card">
                        <div className="label"><FaUserGraduate /> Registered</div>
                        <div className="value">{events.filter(e => e.students?.includes(user?._id)).length}</div>
                    </div>
                </div>

                <div className="card">
                    <h3 className="card-title">My Events</h3>
                    {events.length === 0 ? (
                        <p style={{ color: '#666', padding: '20px', textAlign: 'center' }}>
                            No events available.
                        </p>
                    ) : (
                        events.map(event => {
                            const isRegistered = event.students?.includes(user?._id);
                            const attendanceStatus = event.attendance?.[user?._id] || 'Not marked';
                            
                            return (
                                <div key={event._id} style={{
                                    background: '#f8f9fa',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    marginBottom: '10px'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap' }}>
                                        <div>
                                            <h4 style={{ color: 'var(--srm-blue)' }}>{event.name}</h4>
                                            <p style={{ fontSize: '13px', color: '#666' }}>{event.synopsis}</p>
                                            <p style={{ fontSize: '12px', color: '#999' }}>
                                                📅 {new Date(event.date).toLocaleDateString()} at {event.time}
                                                {event.venue && ` 📍 ${event.venue}`}
                                            </p>
                                            {event.coordinators?.length > 0 && (
                                                <p style={{ fontSize: '12px', color: '#666' }}>
                                                    👥 Coordinators:&nbsp;
                                                    {event.coordinators.map((c, index) => (
                                                        <span key={c._id}>
                                                            {c._id === event.createdBy?._id ? (
                                                                <span style={{ color: '#0a1e3c', fontWeight: 'bold' }}>⭐ {c.name} (Primary)</span>
                                                            ) : (
                                                                <span style={{ color: '#17a2b8' }}>👤 {c.name}</span>
                                                            )}
                                                            {index < event.coordinators.length - 1 && ', '}
                                                        </span>
                                                    ))}
                                                </p>
                                            )}
                                            {event.poster && (
                                                <div style={{ marginTop: '8px' }}>
                                                    <img
                                                        src={`${getStaticBaseUrl()}/${event.poster}`}
                                                        alt="Event Poster"
                                                        style={{ 
                                                            maxWidth: '100px', 
                                                            maxHeight: '100px', 
                                                            borderRadius: '8px', 
                                                            cursor: 'pointer',
                                                            border: '1px solid #ddd'
                                                        }}
                                                        onClick={() => handleViewPoster(event.poster)}
                                                    />
                                                    <span style={{ fontSize: '11px', color: '#999', display: 'block' }}>
                                                        Click poster to enlarge
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span className={`badge ${isRegistered ? 'badge-success' : 'badge-danger'}`}>
                                                {isRegistered ? '✓ Registered' : 'Not Registered'}
                                            </span>
                                            {event.status === 'completed' && (
                                                <div style={{ marginTop: '5px' }}>
                                                    <span className={`badge ${
                                                        attendanceStatus === 'present' ? 'badge-success' :
                                                        attendanceStatus === 'absent' ? 'badge-danger' :
                                                        'badge-warning'
                                                    }`}>
                                                        {attendanceStatus === 'present' ? <FaCheckCircle /> : <FaTimesCircle />}
                                                        {' '}{attendanceStatus}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Poster Popup Modal */}
            {showPosterPopup && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    zIndex: 99999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    padding: '20px'
                }} onClick={() => setShowPosterPopup(false)}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '20px',
                        maxWidth: '90%',
                        maxHeight: '90%',
                        position: 'relative'
                    }} onClick={(e) => e.stopPropagation()}>
                        <button 
                            onClick={() => setShowPosterPopup(false)}
                            style={{
                                position: 'absolute',
                                top: '-15px',
                                right: '-15px',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                fontSize: '20px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 100,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#c82333'}
                            onMouseLeave={(e) => e.target.style.background = '#dc3545'}
                        >
                            <FaTimes />
                        </button>
                        <img
                            src={`${getStaticBaseUrl()}/${selectedPoster}`}
                            alt="Event Poster"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                borderRadius: '8px',
                                display: 'block',
                                margin: '0 auto'
                            }}
                        />
                        <div style={{ 
                            textAlign: 'center', 
                            marginTop: '10px', 
                            fontSize: '12px', 
                            color: '#666' 
                        }}>
                            Click outside or the X button to close
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentDashboard;